import {useQuery} from '@github-ui/react-query'
import {useRelayEnvironment} from 'react-relay'
import {Environment, fetchQuery, graphql} from 'relay-runtime'

import type {useRepositoryDefaultBranchQuery} from './__generated__/useRepositoryDefaultBranchQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const repositoryDefaultBranchGraphQLQuery = graphql`
  query useRepositoryDefaultBranchQuery($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      defaultBranchRef {
        name
      }
    }
  }
`

/**
 * Hook to fetch the default branch name for a repository.
 * @param owner - The repository owner login
 * @param name - The repository name
 * @returns Query result containing the default branch name or null
 */
export function useRepositoryDefaultBranch(owner?: string, name?: string) {
  const environment = useRelayEnvironment()

  return useQuery({
    queryKey: ['repository-default-branch', JSON.stringify(environment), owner, name],
    queryFn: async () => {
      if (!owner || !name) {
        return null
      }

      return fetchRepositoryDefaultBranch(environment, owner, name)
    },
  })
}

/**
 * Fetches the default branch name for a repository using GraphQL.
 * @param environment - The Relay environment for making GraphQL queries
 * @param owner - The repository owner login
 * @param name - The repository name
 * @returns Promise resolving to the default branch name or null if not found
 */
export const fetchRepositoryDefaultBranch = async (
  environment: Environment,
  owner: string,
  name: string,
): Promise<string | null> => {
  const data = await fetchQuery<useRepositoryDefaultBranchQuery>(
    environment,
    repositoryDefaultBranchGraphQLQuery,
    {
      owner,
      name,
    },
    {fetchPolicy: 'store-or-network'},
  ).toPromise()

  const branchName: string | null | undefined = data?.repository?.defaultBranchRef?.name
  if (typeof branchName === 'string') {
    return branchName
  }
  return null
}
